import { RouterOutput } from "@mindlercare/mindlerapi-client";
import { Icon, useSnackbar } from "@mindlercare/ui-web";
import { DialogContent } from "@mui/material";
import MaterialDialog from "@mui/material/Dialog";
import { mindlerApiVanilla } from "api/apiv2";
import CONSTANTS from "core/constants";
import { logger } from "core/logger";
import { Slot } from "core/types";
import { isAfter, isBefore, fromUnixTime } from "date-fns";
import { useTemplates } from "hooks/useTemplates";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { Typography } from "components/typography";
import { hasPermissions } from "../../../helpers/permissions";
import { hasAnyRole } from "../../../helpers/roles";
import { getTemplate } from "../../../helpers/templates";
import { useCurrentUser } from "../../../hooks/userHooks";
import useMarketStrategy from "../../../strategies/market/useMarketStrategy";
import { useCloseDialog } from "./actions";
import { getSlotMethodFromBookingIntent } from "./helpers";
import { PatientSearch } from "./patientSearch/patientSearch";
import { SlotDateAndStatusSection } from "./slotDateAndStatusSection/slotDateAndStatusSection";
import { SlotDialogActions } from "./slotDialogActions";
import { SlotMeetingLink } from "./slotMeetingLink";
import { SlotPatientData } from "./slotPatientData/slotPatientData";
import { SlotStatusSection } from "./slotStatusSection";
import { SlotTitle } from "./slotTitle";
import { TreatmentStatusSection } from "./treatmentStatusSection";
import { usePatientUserIdFromUrl } from "hooks/usePatientUserIdFromUrl";

export const SlotDialog = ({ slot: slotProp }: { slot: Slot }) => {
  const [slot, setSlot] = useState<Slot>(slotProp);
  const { user } = useCurrentUser();
  const currentTime = new Date();
  const { t } = useTranslation();
  const {
    changeSlotStatus: {
      enabled,
      enabledForPermission,
      enabledForRoles,
      feeStatuses,
      getSlotStatuses,
    },
  } = useMarketStrategy();
  const { data: templates } = useTemplates();
  const slotTemplate = getTemplate(slot.templateId, templates);
  const templateName = slotTemplate?.name;
  const [patientUserIdFromSearchParams] = usePatientUserIdFromUrl();

  const { enqueueSnackbar } = useSnackbar();
  const isOldSlot = isBefore(fromUnixTime(slot.endsAt), new Date());
  const slotIsBookable =
    slot.status === CONSTANTS.BOOKING_STATUS.OPEN && slot.patientId;
  const meetingIsEnded = isAfter(currentTime, fromUnixTime(slot.endsAt));
  const meetingIsCancelled = [
    CONSTANTS.BOOKING_STATUS.CANCELED,
    CONSTANTS.BOOKING_STATUS.LATE_CANCELED,
  ].includes(slot.status);
  const isEnabledByPermission = hasPermissions(user, enabledForPermission);
  const isEnabledByRole = hasAnyRole(user?.roles ?? [], enabledForRoles);
  const isPatientBookableWithoutMeeting =
    templateName === CONSTANTS.SLOT_TEMPLATES.DIAGNOSTICS_JOURNAL_ENTRY;
  const isMeeting = slotTemplate?.isMeeting;
  const hasUpdatableBookingStatus = Object.values(
    CONSTANTS.UPDATABLE_BOOKING_STATUS,
  ).includes(slot.status);
  const closeDialogWithoutHistory = useCloseDialog(false);
  const showPatientSearch =
    !patientUserIdFromSearchParams &&
    slot.status === "OPEN" &&
    ((!isOldSlot && isMeeting) || isPatientBookableWithoutMeeting);
  const showPatientSection = isMeeting || isPatientBookableWithoutMeeting;
  const showVideoMeetingLink =
    !meetingIsCancelled &&
    isBefore(currentTime, fromUnixTime(slot.endsAt)) &&
    slot.status !== "OPEN" &&
    isMeeting &&
    slot.method === "VIDEO";
  const showPhoneMeetingInfo =
    !meetingIsCancelled &&
    isBefore(currentTime, fromUnixTime(slot.endsAt)) &&
    slot.status !== "OPEN" &&
    isMeeting &&
    slot.method === "PHONE";
  const showStatusSection =
    enabled &&
    (isEnabledByPermission || isEnabledByRole) &&
    (meetingIsEnded || meetingIsCancelled) &&
    hasUpdatableBookingStatus;
  const [bookingIntent, setBookingIntent] =
    useState<RouterOutput["psychologists"]["bookingIntent"]>();

  const slotMethodText = slot.method === "PHONE" ? t("PHONE") : t("VIDEO");

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (slot?.patientId) setSlot(slot);
  }, [slot]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSlot((prevSlot) => ({
      ...prevSlot,
      ...slotProp,
    }));
  }, [slotProp]);

  const onClose = async () => {
    await closeDialogWithoutHistory();
  };

  const handleGetPatientData = async (
    patient: RouterOutput["patients"]["getByUserId"],
  ) => {
    if (!patient) return;

    let slotMethod = slot.method;
    mindlerApiVanilla.psychologists.bookingIntent
      .mutate({
        userId: patient.userId,
        slotId: slot.slotId,
      })
      .then((bookingIntent) => {
        setBookingIntent(bookingIntent);
        const method = getSlotMethodFromBookingIntent("VIDEO", bookingIntent);
        slotMethod = method;
      })
      .catch((error) => {
        logger.error("Failed to get booking intent", error);
        enqueueSnackbar({
          text: t("SOMETHING_WENT_WRONG"),
          isError: true,
        });
      })
      .finally(() => {
        const patientData = {
          ...slot,
          firstName: patient.firstName,
          lastName: patient.lastName,
          patientId: patient.patientId,
          userId: patient.userId,
          phoneNumber: patient.phoneNumber,
          method: slotMethod,
          nationalIdentity: patient.nationalIdentity || null,
        };
        setSlot(patientData);
      });
  };

  return (
    <DialogContainer
      open={true}
      onClose={onClose}
      aria-labelledby="slot-dialog"
    >
      <SlotTitle
        isBookingPatient={bookingIntent != null}
        onSlotMethodUpdate={(slotMethod) =>
          setSlot({ ...slot, method: slotMethod })
        }
        slot={slot}
        bookingIntent={bookingIntent}
        templateName={templateName}
        closeDialog={onClose}
      />
      <DialogContentContainer>
        {showPatientSection && (
          <PatientSection>
            {showPatientSearch && (
              <PatientSearch
                psychologistId={slot.psychologistId}
                handleGetPatientData={handleGetPatientData}
              />
            )}
            <SlotPatientData
              slot={slot}
              handleGetPatientData={handleGetPatientData}
              patientUserIdFromSearchParams={patientUserIdFromSearchParams}
            />
          </PatientSection>
        )}
        {slot.status === CONSTANTS.BOOKING_STATUS.BOOKED && (
          <TreatmentStatusSection slot={slot} />
        )}
        <BookingInfoSection $highlight={!showPatientSection}>
          {slotTemplate?.isMeeting ? (
            <>
              {slot.method === "PHONE" ? (
                <Icon.Phone style={{ marginRight: "8px" }} />
              ) : (
                <Icon.Video style={{ marginRight: "8px" }} />
              )}
              <Typography variant="h3" insensitive>
                {`${slotMethodText} ${t("LOWERCASE_BOOKING_ID")}:`}
              </Typography>
              <Typography variant="h3" insensitive dataTestId="slot-id">
                {slot?.slotId}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h3" insensitive>
                {`${t("BOOKING_ID")}:`}
              </Typography>
              <Typography variant="h3" insensitive dataTestId="slot-id">
                {slot?.slotId}
              </Typography>
            </>
          )}
        </BookingInfoSection>

        <SlotDateAndStatusSection
          slot={slot}
          showBookingInfo={isMeeting === undefined ? true : isMeeting}
        />
        {slotIsBookable && <SlotDialogActions slot={slot} />}
        {showVideoMeetingLink && (
          <BaseSection data-testid="slot-meeting-link">
            <SlotMeetingLink slot={slot} />
          </BaseSection>
        )}
        {showPhoneMeetingInfo && (
          <BaseSection>
            <Typography insensitive variant="h3" sx={{ marginBottom: "10px" }}>
              {t("START_MEETING")}
            </Typography>
            <Typography insensitive color="GRAY_DARK" variant="body-small">
              {t("PHONE_MEETING_INFO")}
            </Typography>
          </BaseSection>
        )}
        {showStatusSection && (
          <SlotStatusSection
            slot={slot}
            feeStatuses={feeStatuses}
            getSlotStatuses={getSlotStatuses}
            setSlotState={(slotId, status, feeWaived) =>
              setSlot({
                ...slot,
                slotId,
                status: status as Slot["status"],
                feeWaived,
              })
            }
          />
        )}
      </DialogContentContainer>
    </DialogContainer>
  );
};

const DialogContainer = styled(MaterialDialog)`
  .MuiDialog-paper {
    display: grid;
    grid-template-rows: auto 1fr;
    width: 736px;

    @media (max-width: ${({ theme }) => theme.BREAKPOINTS.MD}) {
      min-width: auto;
    }
  }
`;

const DialogContentContainer = styled(DialogContent)`
  padding: 0px 0px 40px;
  background-color: ${({ theme }) => theme.COLORS.WHITE};
  display: flex;
  flex-direction: column;
  width: 736px;

  @media (max-width: ${({ theme }) => theme.BREAKPOINTS.MD}) {
    min-width: auto;
  }
`;

const BaseSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 32px 40px 0;
`;

const PatientSection = styled(BaseSection)`
  background-color: ${({ theme }) => theme.COLORS.TERTIARY_LIGHT};
  padding-bottom: 16px;
  width: 100%;
`;

const BookingInfoSection = styled(BaseSection)<{ $highlight?: boolean }>`
  padding: ${({ $highlight }) => ($highlight ? "22px 40px" : "24px 40px 0")};
  display: flex;
  flex-direction: row;
  min-height: ${({ $highlight }) => ($highlight ? "72px" : "auto")};
  gap: 3px;
  background-color: ${({ $highlight, theme }) =>
    $highlight ? theme.COLORS.TERTIARY_LIGHT : "transparent"};
  align-items: center;
`;
